import React, { useState } from "react";
import { App } from "antd5";

import { useSavedSuppliers } from "lib/hooks/api/signals/useSavedSuppliers";
import { useUpdateSignalSettings } from "lib/hooks/api/signals/useUpdateSignalSettings";
import { useCheckSubscription } from "lib/providers/ProHelper";
import { settingsRoutes } from "lib/routes";
import { EventNames, useTracking } from "lib/tracking";
import { isArrayInArray, simpleArrayDedupe } from "lib/utils";
import { usePaywall } from "../../../../lib/providers/Paywall";
import { Item, SaveToList } from "./SaveToList";

export function SaveToCompetitorOrPartner({
  supplierGuids,
  isCreateFormOpen,
  onToggleCreateForm,
}: {
  supplierGuids: string[];
  isCreateFormOpen: boolean;
  onToggleCreateForm: (isCreateFormOpen: boolean) => void;
}) {
  const { message } = App.useApp();
  const { logEvent } = useTracking();

  const { data: savedSuppliers, isLoading, isError } = useSavedSuppliers();
  const [savingItemId, setSavingItemId] = useState<string | undefined>();

  const [searchText, setSearchText] = useState("");
  const { authorised: hasSuppliers } = useCheckSubscription("SUPPLIERS", {
    "Context source": "Save Supplier",
  });
  const { openPaywallModal } = usePaywall();

  const { partnerGuids, competitorGuids } = savedSuppliers || {
    partnerGuids: [],
    competitorGuids: [],
  };
  // Saved only when every selected supplier is saved, so a mixed selection offers to save the
  // rest rather than unsaving the one that is already there. `isArrayInArray` is vacuously true
  // for an empty selection, which would offer Unsave with nothing selected.
  const hasSelection = supplierGuids.length > 0;
  const isPartner = hasSelection && isArrayInArray(supplierGuids, partnerGuids);
  const isCompetitor = hasSelection && isArrayInArray(supplierGuids, competitorGuids);

  const { mutate: updateSignalSettings } = useUpdateSignalSettings({
    onSuccess: (_data, variables, _context) => {
      setSavingItemId(undefined);

      const bulkSave = supplierGuids.length > 1;
      logEvent(bulkSave ? EventNames.supplierBulkSaved : EventNames.supplierSaved, {
        supplierGuids,
      });

      const { competitorIds, partnerIds } = variables.input;
      if (competitorIds !== undefined && competitorIds !== null) {
        message.success("Competitor signals saved successfully");
      }
      if (partnerIds !== undefined && partnerIds !== null) {
        message.success("Partner signals saved successfully");
      }
    },
    onError: () => {
      setSavingItemId(undefined);
      message.error("An error occurred saving competitor and partner signals");
    },
  });

  const onClick = (signalType: "competitor" | "partner") => {
    if (!hasSuppliers) {
      openPaywallModal(signalType === "competitor" ? "COMPETITORS" : "PARTNERS", {
        contextSource: "Save Supplier",
      });
      return;
    }

    setSavingItemId(signalType);

    if (signalType === "competitor") {
      if (isCompetitor) {
        updateSignalSettings({
          input: {
            competitorIds: competitorGuids.filter(
              (competitorGuid) => !supplierGuids.includes(competitorGuid),
            ),
          },
        });
      } else {
        updateSignalSettings({
          input: { competitorIds: simpleArrayDedupe([...competitorGuids, ...supplierGuids]) },
        });
      }
    }
    if (signalType === "partner") {
      if (isPartner) {
        updateSignalSettings({
          input: {
            partnerIds: partnerGuids.filter((partnerId) => !supplierGuids.includes(partnerId)),
          },
        });
      } else {
        updateSignalSettings({
          input: { partnerIds: simpleArrayDedupe([...partnerGuids, ...supplierGuids]) },
        });
      }
    }
  };

  const allItems = [
    {
      label: "My competitors",
      isSaved: isCompetitor,
      id: "competitor",
      viewUrl: settingsRoutes.signalSettings,
      paywall: !hasSuppliers,
    },
    {
      label: "My partners",
      isSaved: isPartner,
      id: "partner",
      viewUrl: settingsRoutes.signalSettings,
      paywall: !hasSuppliers,
    },
  ] as Item[];
  const filteredItems = filterBySearchText(searchText, allItems);

  return (
    <SaveToList
      items={filteredItems}
      loading={{
        lists: isLoading,
        itemId: savingItemId,
      }}
      isError={isError}
      onItemClick={(id, _) => onClick(id as "competitor" | "partner")}
      entityIds={supplierGuids}
      entityType="supplier"
      onSearch={(text) => setSearchText(text)}
      isCreateFormOpen={isCreateFormOpen}
      onToggleCreateForm={onToggleCreateForm}
    />
  );
}

function filterBySearchText(searchText: string, allListItems: Item[]) {
  return searchText
    ? allListItems.filter((item) => item.label.toLowerCase().includes(searchText.toLowerCase()))
    : allListItems;
}
